// The SCHEDULER LIST view (Scheduler Screen v2, the "Signal" re-layout): lifecycle tab chips with honest
// counts + a name search, then one CALM Signal row per human Schedule — the lifecycle pill, the name, the
// latest run's outcome as a single toned dot, the next SERVER-projected occurrence, and the recent-window
// landed tally. Every figure derives from the projection (G-182); no cadence phrase renders because the
// wire carries none (P-SELF). Tokens only (G-181); no internal identifier (G-197/G-058).
//
// WHY THE ROW IS THIS SHORT. The owner's complaint (11 Aug 2026) was that the board was "really busy": each
// row carried a lifecycle pill, the name, a coloured chip PER recent run (up to ten of them, since the
// board reads the module's default recent window), a next-run column, a landed tally, a health word AND
// two-to-three action buttons. Three of those were doing the same job badly. So the run history collapsed
// to the LATEST outcome only — the failed/interrupted-vs-completed distinction the module RECORDS survives
// as the dot's tone (G-107/G-111), it just stopped being repeated ten times — the health word went (the
// dot already carries it), and every action moved to the detail panel, which renders the very same
// RoutineActions components. Nothing was lost; it moved one click away, and the click is now the WHOLE ROW.
//
// The full history, the brief, and every control are one click away in RoutineDetail.tsx.

import { useState } from 'react';
import type { RunRecordRow, RunState, ScheduleHealthScheduleRow } from '../projections.ts';
import { Dot, EmptyState, StatusPill, type Tone } from '../primitives.tsx';
import { scheduleProjectionPresentation, stateCounts } from './scheduler-format.ts';

/** Map a single Run Record state to a tone — failed/interrupted are the attention tone (distinct). */
const RUN_TONE: Readonly<Record<RunState, Tone>> = {
  completed: 'go',
  running: 'go',
  failed: 'attention',
  interrupted: 'attention',
};

/** The lifecycle pill vocabulary — Live is the user-facing word for the recorded `active` state. */
const STATE_PILL: Readonly<
  Record<ScheduleHealthScheduleRow['state'], { readonly tone: Tone; readonly label: string }>
> = {
  active: { tone: 'go', label: 'Live' },
  paused: { tone: 'attention', label: 'Paused' },
  draft: { tone: 'neutral', label: 'Draft' },
  finished: { tone: 'neutral', label: 'Finished' },
  archived: { tone: 'neutral', label: 'Archived' },
};

/** The list tab set: everything current, or one lifecycle state (client view-state only). ARCHIVED routines
 *  sit under their own tab and nowhere else — "All" is the current board, and the calendar never places
 *  them (an archived routine has no next run). */
export type RoutineTab = 'all' | 'active' | 'paused' | 'draft' | 'archived';
const TABS: readonly { readonly id: RoutineTab; readonly label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Live' },
  { id: 'paused', label: 'Paused' },
  { id: 'draft', label: 'Drafts' },
  { id: 'archived', label: 'Archived' },
];

/** The LATEST run's outcome, and only that one — a toned dot and the recorded state word. Failed and
 *  interrupted carry the attention tone, which is the distinction the module RECORDS, never a UI heuristic
 *  (G-107/G-111, G-182). The outcome/error sentence is not repeated here; the panel carries it in full. */
function LatestRun({ run }: { readonly run: RunRecordRow }): React.JSX.Element {
  const tone = RUN_TONE[run.state];
  return (
    <span className="rr-last" data-run-state={run.state} data-run-tone={tone}>
      <Dot tone={tone} />
      <span className="lb">{run.state}</span>
    </span>
  );
}

/** One CALM Signal routine row: pill, name, latest outcome, next run, landed tally — and no controls. The
 *  whole row is ONE button that opens the detail panel, so the owner can line the routines up and click
 *  straight through them; because the row holds no other control, nothing interactive nests inside it. */
function RoutineRow({
  row,
  onOpen,
  selected,
}: {
  readonly row: ScheduleHealthScheduleRow;
  readonly onOpen: (scheduleId: string) => void;
  readonly selected: boolean;
}): React.JSX.Element {
  const pill = STATE_PILL[row.state];
  const next = row.nextOccurrences[0];
  const latest = row.recentRuns[0];
  const projection = scheduleProjectionPresentation(row.state);
  return (
    <li className={selected ? 'rt-row sel' : 'rt-row'} data-health={row.health}>
      <button
        type="button"
        className="rr-open"
        aria-label={`Open routine: ${row.name}`}
        aria-current={selected ? 'true' : undefined}
        onClick={() => onOpen(row.scheduleId)}
      >
        <span className="rr-pill">
          <StatusPill tone={pill.tone}>{pill.label}</StatusPill>
        </span>
        <span className="rr-main">
          <span className="rr-name">{row.name}</span>
          {latest !== undefined ? <LatestRun run={latest} /> : null}
        </span>
        <span className="rr-next">
          <span className="nx">
            {next !== undefined ? `${next.weekday} ${next.localDate}` : 'none projected'}
          </span>
          <span className="mono-label">{projection.listCaption}</span>
        </span>
        <span className="rr-runs">
          <span className="ct">{row.outcomeTally.completed}</span>
          <span className="mono-label">landed</span>
        </span>
      </button>
    </li>
  );
}

/** The lifecycle tab chips + the client-side name search. */
function ListControls({
  schedules,
  tab,
  onTab,
  search,
  onSearch,
}: {
  readonly schedules: readonly ScheduleHealthScheduleRow[];
  readonly tab: RoutineTab;
  readonly onTab: (tab: RoutineTab) => void;
  readonly search: string;
  readonly onSearch: (value: string) => void;
}): React.JSX.Element {
  const counts = stateCounts(schedules);
  const countFor: Readonly<Record<RoutineTab, number>> = {
    all: schedules.length - counts.archived,
    active: counts.live,
    paused: counts.paused,
    draft: counts.draft,
    archived: counts.archived,
  };
  return (
    <div className="rt-tabs">
      {TABS.map((entry) => (
        <button
          key={entry.id}
          type="button"
          className="rt-tab"
          aria-pressed={tab === entry.id}
          data-tab={entry.id}
          onClick={() => onTab(entry.id)}
        >
          <span>{entry.label}</span>
          <span className="cnt">{countFor[entry.id]}</span>
        </button>
      ))}
      <span className="spacer" aria-hidden="true" />
      <input
        type="search"
        className="rt-search"
        placeholder="Find a routine"
        aria-label="Search routines"
        value={search}
        onChange={(event) => onSearch(event.target.value)}
      />
    </div>
  );
}

/** Narrow + order the rows client-side only (the projection stays whole): the lifecycle tab, the name
 *  search, then soonest next occurrence first. */
function visibleRows(
  schedules: readonly ScheduleHealthScheduleRow[],
  tab: RoutineTab,
  search: string,
): readonly ScheduleHealthScheduleRow[] {
  const needle = search.trim().toLowerCase();
  return schedules
    .filter((row) => (tab === 'all' ? row.state !== 'archived' : row.state === tab))
    .filter((row) => needle.length === 0 || row.name.toLowerCase().includes(needle))
    .sort((a, b) => {
      const an = a.nextOccurrences[0]?.instant ?? '9999';
      const bn = b.nextOccurrences[0]?.instant ?? '9999';
      return an < bn ? -1 : 1;
    });
}

/** What the list view renders. It routes nothing itself any more — the routed per-routine intents reach
 *  the detail panel, which is where every control now lives. */
export interface RoutineListProps {
  readonly schedules: readonly ScheduleHealthScheduleRow[];
  readonly tab: RoutineTab;
  readonly onTab: (tab: RoutineTab) => void;
  /** Open one routine in the detail panel (the surface owns which is open). */
  readonly onOpen: (scheduleId: string) => void;
  /** The routine currently open in the detail panel, if any (row highlight only). */
  readonly openScheduleId: string | null;
}

/** The list view: tabs + search over the human Schedules, soonest next occurrence first. */
export function RoutineList({
  schedules,
  tab,
  onTab,
  onOpen,
  openScheduleId,
}: RoutineListProps): React.JSX.Element {
  const [search, setSearch] = useState('');
  const shown = visibleRows(schedules, tab, search);
  return (
    <div className="rt-listwrap">
      <ListControls
        schedules={schedules}
        tab={tab}
        onTab={onTab}
        search={search}
        onSearch={setSearch}
      />
      {shown.length === 0 ? (
        <EmptyState>No routine matches this view.</EmptyState>
      ) : (
        <ul className="rt-list">
          {shown.map((row) => (
            <RoutineRow
              key={row.scheduleId}
              row={row}
              onOpen={onOpen}
              selected={row.scheduleId === openScheduleId}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
